/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@compiled/react';
import Button from '@atlaskit/button';
import EditorCloseIcon from '@atlaskit/icon/core/migration/cross--editor-close';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useFullscreenQueryParameter } from '@atlassian/jira-polaris-common/src/controllers/route/index.tsx';
import { useArchiveIssueActions } from '../../controllers/main.tsx';
import { useArchiveIssueStates, getNumberOfSelectedIssues } from '../../controllers/selectors.tsx';
import { messages } from './messages.tsx';
import UnarchiveIdeasButton from './unarchive-issues/main.tsx';

export const IssueManageBar = () => {
	const { formatMessage } = useIntl();
	const fullscreen = useFullscreenQueryParameter();
	const state = useArchiveIssueStates();
	const selectedIssueCount = getNumberOfSelectedIssues({ state });
	const { clearAllSelections } = useArchiveIssueActions();

	if (selectedIssueCount === 0) {
		return null;
	}

	return (
		<div
			css={[
				controlsButtonContainerBaseStyles,
				fullscreen && controlsButtonContainerFullScreenStyles,
			]}
			data-testid="issue-archival-archives.ui.manage-bars.issue-manage-bar"
		>
			<div css={ControlsButtonContent}>
				<div css={LabelContainer}>
					<Button
						appearance="subtle"
						spacing="none"
						iconBefore={
							<EditorCloseIcon
								label={formatMessage(messages.closeButtonLabel)}
								LEGACY_size="medium"
								spacing="spacious"
							/>
						}
						onClick={() => clearAllSelections()}
					/>
					<div css={Label}>{formatMessage(messages.label, { count: selectedIssueCount })}</div>
				</div>
				<div css={ButtonsWrapper}>
					<UnarchiveIdeasButton />
				</div>
			</div>
		</div>
	);
};

const controlsButtonContainerBaseStyles = css({
	position: 'fixed',
	bottom: token('space.500'),
	zIndex: 3,
	left: 'calc(var(--leftSidebarWidth, 0px) + 16px)',
	width: '100%',
	maxWidth: 'calc(100vw - var(--leftSidebarWidth) - 32px)',
	display: 'flex',
	justifyContent: 'center',
});
const controlsButtonContainerFullScreenStyles = css({
	left: 0,
	maxWidth: '100%',
});

const ControlsButtonContent = css({
	position: 'absolute',
	bottom: '0',
	backgroundColor: token('color.background.neutral.bold.pressed'),
	boxShadow: token('elevation.shadow.overlay'),
	borderRadius: token('border.radius.200'),
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
	color: token('color.text.inverse'),
	display: 'flex',
	alignItems: 'stretch',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	button: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		color: `${token('color.text.inverse')} !important`,
		'&:hover': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			color: `${token('color.text.inverse')} !important`,
			backgroundColor: token('color.background.neutral.bold.hovered'),
		},
		'&:active': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			color: `${token('color.text.inverse')} !important`,
			backgroundColor: token('color.background.neutral.bold'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'&[disabled]': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			color: `${token('color.text.subtlest')} !important`,
		},
	},
});

const ButtonsWrapper = css({
	display: 'flex',
	alignItems: 'center',
	flexWrap: 'wrap',
	gap: token('space.100'),
	paddingLeft: token('space.100'),
	paddingRight: token('space.100'),
	position: 'relative',

	'&::before': {
		content: '',
		top: '3px',
		position: 'absolute',
		bottom: '3px',
		width: '1px',
		left: '0px',
		backgroundColor: token('color.border.accent.gray'),
	},
});

const LabelContainer = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	paddingRight: token('space.100'),
});

const Label = css({
	font: token('font.heading.xsmall'),
	paddingRight: token('space.200'),
});
